import { useTranslation } from 'react-i18next';
import { ArrowRight, Lock } from 'lucide-react';
import type { BookingResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import type { BookingActionFlags } from './bookingActionRules';
import { EarlyDeparturePlanCard } from './EarlyDeparturePlanCard';
import { channelName, isOta } from './helpers';

export type BookingActionKind =
  | 'reassign'
  | 'upgrade'
  | 'edit'
  | 'cancel'
  | 'early'
  | 'message'
  | 'payments';

interface Props {
  booking: BookingResponse;
  flags: BookingActionFlags;
  financeVisible?: boolean;
  onPick: (action: BookingActionKind) => void;
  onCancelPlan: () => void;
  planPending: boolean;
}

const STYLE = {
  main: 'bg-ink text-white',
  plain: 'border-[1.5px] border-ink text-ink',
  danger: 'border-[1.5px] border-sealwax text-sealwax',
};

/**
 * Aktionen der Buchung (Board „Buchung“): Apartment wechseln, Aufpreis, bei manuellen Buchungen
 * Bearbeiten und Stornieren, bei Kanalbuchungen die vorzeitige Abreise oder ihr Plan.
 */
export function BookingActionList({
  booking,
  flags,
  financeVisible = false,
  onPick,
  onCancelPlan,
  planPending,
}: Props) {
  const { t } = useTranslation('stripePayments');
  const ota = isOta(booking);
  const channel = channelName(booking.channel);
  const items: Array<{ kind: BookingActionKind; label: string; style: keyof typeof STYLE }> = [];
  if (flags.message) items.push({ kind: 'message', label: 'Nachricht schreiben', style: 'plain' });
  if (flags.reassign) items.push({ kind: 'reassign', label: 'Apartment wechseln', style: 'main' });
  if (flags.upgrade)
    items.push({
      kind: 'upgrade',
      label: 'Aufpreis anfordern',
      style: flags.reassign ? 'plain' : 'main',
    });
  if (flags.editOrCancel || flags.historicalEdit)
    items.push({ kind: 'edit', label: 'Bearbeiten', style: 'plain' });
  if (flags.editOrCancel || flags.finishCancel)
    items.push({
      kind: 'cancel',
      label: flags.editOrCancel ? 'Stornieren' : 'Storno abschließen',
      style: 'danger',
    });
  if (flags.earlyDeparture && !flags.plan)
    items.push({ kind: 'early', label: 'Vorzeitige Abreise', style: 'plain' });
  if (financeVisible) items.push({ kind: 'payments', label: t('title'), style: 'plain' });
  return (
    <div className="flex flex-col gap-2">
      {items.map(({ kind, label, style }) => (
        <button
          key={kind}
          type="button"
          onClick={() => onPick(kind)}
          className={cn(
            'rounded-pill flex h-12 items-center justify-between gap-3 px-4 text-sm font-bold',
            STYLE[style],
          )}
        >
          {label}
          <ArrowRight size={16} aria-hidden />
        </button>
      ))}
      {flags.plan && (
        <EarlyDeparturePlanCard
          plan={flags.plan}
          onEdit={() => onPick('early')}
          onCancel={onCancelPlan}
          pending={planPending}
        />
      )}
      {flags.any && (
        <p className="text-ink-soft flex items-start gap-1.5 text-xs">
          {ota && <Lock size={13} aria-hidden className="mt-px flex-none" />}
          {ota
            ? `Daten, Personen und Storno ändert der Gast über ${channel}.`
            : 'Stornieren zeigt vorher, was über Stripe erstattet wird.'}
        </p>
      )}
    </div>
  );
}
